import { always, anyPass, map, pipe } from 'ramda';
import { isString, isArray } from 'ramda-adjunct';
import { InferableComponentEnhancerWithProps, withPropsOnChange } from 'recompose';
import withLifecycle from '@hocs/with-lifecycle';

type TNameGetter<T> = (prevProps: T, nextProps: T) => boolean;
type TName<T> = string | string[] | TNameGetter<T>;
const hasChanged = (name) => (prevProps, nextProps) => prevProps[name] !== nextProps[name];
const hasChangedInArray = (names: string[]) => anyPass(map(hasChanged, names));

const createPropChangeValidator = <T>(name: TName<T>): TNameGetter<T> =>
  isString(name) ? hasChanged(name) : isArray(name) ? hasChangedInArray(name) : name;

export const onPropChange = <T>(
  name: TName<T>,
  onChange: (prop: T) => void,
  skipOnMount: boolean = false,
): InferableComponentEnhancerWithProps<any, T> => {
  const didPropChange = createPropChangeValidator(name);

  return skipOnMount
    ? withLifecycle<T>({
        onDidUpdate(prevProps, nextProps) {
          if (didPropChange(prevProps, nextProps)) {
            onChange(nextProps);
          }
        },
      })
    : withPropsOnChange<{}, T>(didPropChange, pipe(onChange, always({})));
};

type TIsValueChanged = <T>(getter: (props: T) => any) => TNameGetter<T>;
export const isValueChanged: TIsValueChanged = (getter) => (prevProps, nextProps) =>
  getter(prevProps) !== getter(nextProps);
